import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useGetPageHtmlQuery, useGeneratePageHtmlMutation, LandingPage } from '../../api/pagesApi';

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const PreviewModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 12px 12px 0 0;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
    color: white;
    &:hover {
      background: linear-gradient(135deg, #FF5252 0%, #FF7575 100%);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    &:hover {
      background: #f9fafb;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: #6b7280;
  font-size: 16px;
`;

const CustomDomainPreview = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  text-align: center;
  padding: 40px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);

  h3 {
    margin: 0 0 16px 0;
    color: #1f2937;
    font-size: 24px;
    font-weight: 600;
  }

  p {
    margin: 0 0 24px 0;
    color: #6b7280;
    font-size: 16px;
    line-height: 1.5;
    max-width: 500px;
  }
`;

const DomainDisplay = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px 24px;
  margin: 0 0 24px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DomainLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DomainValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  font-family: monospace;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
`;

const ExternalIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.7;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: #ef4444;
  text-align: center;
  padding: 40px;

  h4 {
    margin: 0 0 12px 0;
    color: #dc2626;
  }

  p {
    margin: 0 0 20px 0;
    color: #6b7280;
  }
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const DeviceSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const DeviceButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #d1d5db;

  ${props => props.active ? `
    background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%);
    color: white;
    border-color: #FF6B6B;
  ` : `
    background: white;
    color: #374151;
    &:hover {
      background: #f9fafb;
    }
  `}
`;

const FrameContainer = styled.div<{ device: string }>`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f3f4f6;

  ${props => {
    switch (props.device) {
      case 'mobile':
        return `
          .preview-frame {
            width: 375px;
            height: 667px;
            border: 2px solid #374151;
            border-radius: 12px;
            background: white;
          }
        `;
      case 'tablet':
        return `
          .preview-frame {
            width: 768px;
            height: 1024px;
            border: 2px solid #374151;
            border-radius: 8px;
            background: white;
          }
        `;
      default:
        return `
          .preview-frame {
            width: 100%;
            height: 100%;
            border: none;
            background: white;
          }
        `;
    }
  }}
`;

interface PagePreviewProps {
  page: LandingPage;
  onClose: () => void;
}

export const PagePreview: React.FC<PagePreviewProps> = ({ page, onClose }) => {
  const [device, setDevice] = useState('desktop');
  const [htmlContent, setHtmlContent] = useState<string>('');
  
  // Check if this is an external domain page (template: 'external' with custom domain)
  const isExternalDomainPage = page.template === 'external' && !!page.customDomain;
  
  const { 
    data: htmlData, 
    isLoading, 
    error,
    refetch 
  } = useGetPageHtmlQuery(page.id, {
    skip: isExternalDomainPage // Skip API call for external domain pages
  });

  const [generateHtml, { isLoading: isGenerating }] = useGeneratePageHtmlMutation();

  useEffect(() => {
    if (htmlData?.htmlContent) {
      setHtmlContent(htmlData.htmlContent);
    }
  }, [htmlData]);

  const handleGenerateHtml = async () => {
    try {
      const result = await generateHtml(page.id).unwrap();
      setHtmlContent(result.htmlContent);
    } catch (error) {
      console.error('Failed to generate HTML:', error);
    }
  };

  const handleOpenInNewTab = () => {
    if (isExternalDomainPage && page.customDomain) {
      // For external domain pages, open the custom domain directly
      const protocol = page.customDomain.startsWith('http') ? '' : 'https://';
      window.open(`${protocol}${page.customDomain}`, '_blank');
    } else if (htmlData?.url) {
      // Open the server-rendered URL in a new tab
      window.open(`${htmlData.url}`, '_blank');
    } else {
      // Fallback to basic URL
      window.open(`https://chala-flow-887539836693.us-central1.run.app/${page.slug}`, '_blank');
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const createBlobUrl = (html: string) => {
    const blob = new Blob([html], { type: 'text/html' });
    return URL.createObjectURL(blob);
  };

  const renderCustomDomainPreview = () => (
    <CustomDomainPreview>
      <ExternalIcon>🌐</ExternalIcon>
      <h3>External Domain Page</h3>
      <p>
        This is an external domain page that redirects to your custom domain. 
        No HTML content is generated for this type of page.
      </p>
      <DomainDisplay>
        <DomainLabel>Custom Domain</DomainLabel>
        <DomainValue>{page.customDomain}</DomainValue>
      </DomainDisplay>
      <ActionButton 
        variant="primary" 
        onClick={handleOpenInNewTab}
      >
        Visit Domain
      </ActionButton>
    </CustomDomainPreview>
  );

  // Show custom domain preview for external domain pages
  if (isExternalDomainPage) {
    return (
      <PreviewContainer>
        <PreviewModal>
          <PreviewHeader>
            <PreviewTitle>Preview - {page.title}</PreviewTitle>
            <PreviewActions>
              <ActionButton onClick={handleOpenInNewTab}>
                Visit Domain
              </ActionButton>
              <CloseButton onClick={onClose}>×</CloseButton>
            </PreviewActions>
          </PreviewHeader>
          <PreviewContent>
            {renderCustomDomainPreview()}
          </PreviewContent>
        </PreviewModal>
      </PreviewContainer>
    );
  }

  if (isLoading) {
    return (
      <PreviewContainer>
        <PreviewModal>
          <PreviewHeader>
            <PreviewTitle>Loading Preview - {page.title}</PreviewTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </PreviewHeader>
          <LoadingState>
            Loading server-rendered HTML preview...
          </LoadingState>
        </PreviewModal>
      </PreviewContainer>
    );
  }

  if (error && !htmlContent) {
    return (
      <PreviewContainer>
        <PreviewModal>
          <PreviewHeader>
            <PreviewTitle>Preview Error - {page.title}</PreviewTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </PreviewHeader>
          <ErrorState>
            <h4>Failed to Load Preview</h4>
            <p>The server-rendered HTML could not be loaded. This might be because the page hasn't been generated yet.</p>
            <ActionButton 
              variant="primary" 
              onClick={handleGenerateHtml} 
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate HTML'}
            </ActionButton>
          </ErrorState>
        </PreviewModal>
      </PreviewContainer>
    );
  }

  return (
    <PreviewContainer>
      <PreviewModal>
        <PreviewHeader>
          <PreviewTitle>Preview - {page.title}</PreviewTitle>
          <PreviewActions>
            <DeviceSelector>
              <DeviceButton 
                active={device === 'desktop'} 
                onClick={() => setDevice('desktop')}
              >
                Desktop
              </DeviceButton>
              <DeviceButton 
                active={device === 'tablet'} 
                onClick={() => setDevice('tablet')}
              >
                Tablet
              </DeviceButton>
              <DeviceButton 
                active={device === 'mobile'} 
                onClick={() => setDevice('mobile')}
              >
                Mobile
              </DeviceButton>
            </DeviceSelector>
            <ActionButton onClick={handleRefresh}>
              Refresh
            </ActionButton>
            <ActionButton onClick={handleOpenInNewTab}>
              Open in New Tab
            </ActionButton>
            <CloseButton onClick={onClose}>×</CloseButton>
          </PreviewActions>
        </PreviewHeader>
        <PreviewContent>
          <FrameContainer device={device}>
            {htmlContent ? (
              <PreviewFrame
                className="preview-frame"
                src={createBlobUrl(htmlContent)}
                title={`Preview of ${page.title}`}
              />
            ) : (
              <LoadingState>
                No HTML content available. Click "Generate HTML" to create the preview.
              </LoadingState>
            )}
          </FrameContainer>
        </PreviewContent>
      </PreviewModal>
    </PreviewContainer>
  );
};